import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useSelector } from "react-redux";
import { PapiroConsole } from '../utils/papiroConsole';
import CustomizedSnackbars from "../components/material-snackbars";
import Sound from "../assets/audio.wav";

export const AgentWorkspaceHubContext = createContext();

function AgentWorkspaceHubProvider(props) {
  const userSession = useSelector((state) => state.userSession);

  const [agentWorkspaceHub, setAgentWorkspaceHub] = useState(null);
  const [queue, setQueue] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [agentStatus, setAgentStatus] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);

  // Lista de notificações recentes para o dropdown
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

  // Refs para callbacks - permite que componentes se registrem para receber atualizações
  const queueChangeCallbacksRef = useRef(new Set());
  const newItemCallbacksRef = useRef(new Set());
  const itemRemovedCallbacksRef = useRef(new Set());
  const itemUpdatedCallbacksRef = useRef(new Set());
  const itemClosedCallbacksRef = useRef(new Set());
  const notificationCallbacksRef = useRef(new Set());

  // Ref para queue anterior (para detectar mudanças)
  const prevQueueRef = useRef([]);

  const [alertNotification, setAlertNotification] = useState({ variant: "info", isOpen: false, message: "" });
  const closeAlertMessage = () => setAlertNotification({ ...alertNotification, isOpen: false });
  const openAlertMessage = (variant, message) => setAlertNotification({ variant, message, isOpen: true });

  const setAgentWorkspaceHubConnection = (connection) => {
    setAgentWorkspaceHub(connection);
    setIsConnected(true);
  };

  // ============================================
  // Sistema de Notificações Recentes
  // ============================================

  const normalizeNotification = (notification) => {
    if (!notification) {
      return notification;
    }

    return {
      ...notification,
      id: notification.id ?? notification.Id,
      called: notification.called ?? notification.Called,
      organization: notification.organization ?? notification.Organization,
      accompaniment: notification.accompaniment ?? notification.Accompaniment,
      calledId: notification.calledId ?? notification.calledid ?? notification.CalledId,
      createdAt: notification.createdAt ?? notification.createdat ?? notification.CreatedAt,
      organizationId: notification.organizationId ?? notification.organizationid ?? notification.OrganizationId,
      accompanimentId: notification.accompanimentId ?? notification.accompanimentid ?? notification.AccompanimentId,
      recipientUserId: notification.recipientUserId ?? notification.recipientuserid ?? notification.RecipientUserId,
      notificationType: notification.notificationType ?? notification.notificationtype ?? notification.NotificationType,
      information: notification.information ?? notification.Information,
      required: notification.required ?? notification.Required,
      finish: notification.finish ?? notification.Finish,
      redirection: notification.redirection ?? notification.Redirection,
    };
  };

  const normalizeNotifications = (notifications) => {
    if (!Array.isArray(notifications)) {
      return notifications;
    }

    return notifications.map(normalizeNotification);
  };

  const setAllNotifications = useCallback((notifications) => {
    PapiroConsole.log("AgentWorkspace: ListNotifications received");
    PapiroConsole.log(notifications);

    if (userSession?.user?.id) {
      localStorage.setItem('notificationMenuRefreshed_' + userSession.user.id, true);
    }

    setNotifications(normalizeNotifications(notifications));
  }, [userSession]);

  const handleListNotifications = useCallback((notifications) => {
    setAllNotifications(notifications);
  }, [setAllNotifications]);

  const addNotification = useCallback((notification) => {
    const newNotif = {
      ...notification,
      id: notification.id || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: notification.timestamp || new Date(),
      read: false,
    };

    setRecentNotifications(prev => {
      // Evitar duplicatas
      const exists = prev.some(n => n.id === newNotif.id);
      if (exists) return prev;
      // Manter no máximo 50 notificações
      return [newNotif, ...prev].slice(0, 50);
    });

    setUnreadNotificationCount(prev => prev + 1);

    // Notificar callbacks registrados
    notificationCallbacksRef.current.forEach(cb => {
      try { cb(newNotif); } catch (e) { PapiroConsole.log("Notification callback error", e); }
    });
  }, []);

  const markNotificationAsRead = useCallback((notificationId) => {
    setRecentNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
    setUnreadNotificationCount(prev => Math.max(0, prev - 1));
  }, []);

  const markAllNotificationsAsRead = useCallback(() => {
    setRecentNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadNotificationCount(0);
  }, []);

  const clearNotifications = useCallback(() => {
    setRecentNotifications([]);
    setUnreadNotificationCount(0);
  }, []);

  // ============================================
  // Sistema de Callbacks para Componentes
  // ============================================

  const registerQueueChangeCallback = useCallback((callback) => {
    queueChangeCallbacksRef.current.add(callback);
    return () => queueChangeCallbacksRef.current.delete(callback);
  }, []);

  const registerNewItemCallback = useCallback((callback) => {
    newItemCallbacksRef.current.add(callback);
    return () => newItemCallbacksRef.current.delete(callback);
  }, []);

  const registerItemRemovedCallback = useCallback((callback) => {
    itemRemovedCallbacksRef.current.add(callback);
    return () => itemRemovedCallbacksRef.current.delete(callback);
  }, []);

  const registerItemUpdatedCallback = useCallback((callback) => {
    itemUpdatedCallbacksRef.current.add(callback);
    return () => itemUpdatedCallbacksRef.current.delete(callback);
  }, []);

  const registerItemClosedCallback = useCallback((callback) => {
    itemClosedCallbacksRef.current.add(callback);
    return () => itemClosedCallbacksRef.current.delete(callback);
  }, []);

  const registerNotificationCallback = useCallback((callback) => {
    notificationCallbacksRef.current.add(callback);
    return () => notificationCallbacksRef.current.delete(callback);
  }, []);

  // Notificar callbacks quando queue muda
  useEffect(() => {
    const prevQueue = prevQueueRef.current;
    const currentQueue = queue;

    // Só notifica se realmente mudou
    if (prevQueue !== currentQueue && currentQueue.length > 0) {
      queueChangeCallbacksRef.current.forEach(cb => {
        try { cb(currentQueue, prevQueue); } catch (e) { PapiroConsole.log("Queue callback error", e); }
      });
    }

    prevQueueRef.current = currentQueue;
  }, [queue]);

  // ============================================
  // Helpers
  // ============================================

  const playNotificationSound = useCallback(() => {
    try {
      const audio = new Audio(Sound);
      audio.play().catch(err => {
        PapiroConsole.log("Audio play prevented (user interaction required)", err);
      });
    } catch (err) {
      PapiroConsole.log("Error playing notification sound", err);
    }
  }, []);

  // Desktop Notification (notificação nativa do navegador)
  const showDesktopNotification = useCallback((title, options = {}) => {
    // Verifica se preferência está habilitada
    if (!preferences?.desktopnotificationsenabled) return;

    // Verifica se browser suporta
    if (!("Notification" in window)) return;

    // Verifica se tem permissão
    if (Notification.permission !== "granted") return;

    try {
      const notification = new Notification(title, {
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: options.tag || 'agent-workspace',
        ...options
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
        if (options.onClick) options.onClick();
      };

      if (!options.requireInteraction) {
        setTimeout(() => notification.close(), 5000);
      }
    } catch (err) {
      PapiroConsole.log("Error showing desktop notification", err);
    }
  }, [preferences]);

  // Helper para criar mensagem descritiva do item
  const getItemDescription = useCallback((item) => {
    const isChat = item.itemtype === 1;
    const title = item.displaytitle || item.title || `#${item.itemid}`;
    const requester = item.requestingusername || item.organizationname || 'cliente';

    if (isChat) {
      return `Chat com ${requester}`;
    }
    return `Chamado ${title}`;
  }, []);

  // Helper to normalize urgency group (can be string or number/enum)
  const normalizeUrgencyGroup = (urgencyGroup) => {
    if (urgencyGroup == null) return 'normal';
    if (typeof urgencyGroup === 'number') {
      switch (urgencyGroup) {
        case 0: return 'critical';
        case 1: return 'warning';
        case 2: return 'normal';
        case 3: return 'waiting';
        default: return 'normal';
      }
    }
    if (typeof urgencyGroup === 'string') {
      return urgencyGroup.toLowerCase();
    }
    return 'normal';
  };

  // ============================================
  // SignalR Event Handlers
  // ============================================

  const handleQueueUpdated = useCallback((items) => {
    PapiroConsole.log("AgentWorkspace: QueueUpdated received", items?.length || 0, "items");
    setQueue(items || []);
  }, []);

  const handleNewItem = useCallback((item) => {
    PapiroConsole.log("AgentWorkspace: NewItem received", item);

    if (!item) return;

    // Atualizar queue
    setQueue(prevQueue => {
      const exists = prevQueue.some(q => q.itemid === item.itemid && q.itemtype === item.itemtype);
      if (exists) {
        return prevQueue.map(q =>
          (q.itemid === item.itemid && q.itemtype === item.itemtype) ? item : q
        );
      }
      return [item, ...prevQueue];
    });

    // Criar mensagem descritiva
    const isChat = item.itemtype === 1;
    const itemDescription = getItemDescription(item);
    const message = `${itemDescription} foi atribuído para você`;

    // Adicionar notificação à lista
    addNotification({
      id: `new-item-${item.itemid}-${item.itemtype}-${Date.now()}`,
      type: 'new_item',
      itemId: item.itemid,
      itemType: item.itemtype,
      message,
      item,
    });

    // Tocar som se habilitado
    if (preferences?.soundenabled) {
      playNotificationSound();
    }

    // Exibir snackbar
    openAlertMessage("info", message);

    // Desktop notification
    showDesktopNotification(
      isChat ? "Novo chat atribuído" : "Novo chamado atribuído",
      {
        body: message,
        tag: `new-item-${item.itemid}-${item.itemtype}`,
      }
    );

    // Notificar callbacks registrados
    newItemCallbacksRef.current.forEach(cb => {
      try { cb(item); } catch (e) { PapiroConsole.log("NewItem callback error", e); }
    });
  }, [preferences, playNotificationSound, showDesktopNotification, addNotification, getItemDescription]);

  const handleItemRemoved = useCallback((data) => {
    PapiroConsole.log("AgentWorkspace: ItemRemoved received", data);

    if (!data || !data.itemid) return;

    // Guardar item antes de remover (para notificação)
    let removedItem = null;
    setQueue(prevQueue => {
      removedItem = prevQueue.find(q => q.itemid === data.itemid && q.itemtype === data.itemtype);
      return prevQueue.filter(q => !(q.itemid === data.itemid && q.itemtype === data.itemtype));
    });

    // Notificar callbacks registrados
    itemRemovedCallbacksRef.current.forEach(cb => {
      try { cb(data, removedItem); } catch (e) { PapiroConsole.log("ItemRemoved callback error", e); }
    });
  }, []);

  const handleItemUpdated = useCallback((item) => {
    PapiroConsole.log("AgentWorkspace: ItemUpdated received", item);

    if (!item) return;

    setQueue(prevQueue => prevQueue.map(q =>
      (q.itemid === item.itemid && q.itemtype === item.itemtype) ? item : q
    ));

    // Notificar callbacks registrados
    itemUpdatedCallbacksRef.current.forEach(cb => {
      try { cb(item); } catch (e) { PapiroConsole.log("ItemUpdated callback error", e); }
    });
  }, []);

  // Handler para item fechado de outra tela (cross-screen notification)
  const handleItemClosed = useCallback((data) => {
    PapiroConsole.log("AgentWorkspace: ItemClosed received", data);

    if (!data || !data.itemId) return;

    // Mapear itemType do backend para itemtype do frontend
    const itemType = data.itemType === 'CHAT' ? 1 : 0;

    // Remover item da fila
    let removedItem = null;
    setQueue(prevQueue => {
      removedItem = prevQueue.find(q => q.itemid === data.itemId && q.itemtype === itemType);
      return prevQueue.filter(q => !(q.itemid === data.itemId && q.itemtype === itemType));
    });

    // Criar mensagem de notificação
    const itemDescription = data.itemType === 'CHAT' ? `Conversa #${data.itemId} foi encerrada` : `Chamado #${data.itemId} foi encerrado`;
    const message = `${itemDescription}.`;

    // Adicionar notificação
    addNotification({
      id: `item-closed-${data.itemId}-${itemType}-${Date.now()}`,
      type: 'item_closed',
      itemId: data.itemId,
      itemType: itemType,
      message,
      data,
    });

    // Exibir snackbar
    openAlertMessage("info", message);

    // Notificar callbacks registrados
    itemClosedCallbacksRef.current.forEach(cb => {
      try { cb(data, removedItem); } catch (e) { PapiroConsole.log("ItemClosed callback error", e); }
    });
  }, [addNotification]);

  const handleMetricsUpdated = useCallback((newMetrics) => {
    PapiroConsole.log("AgentWorkspace: MetricsUpdated received", newMetrics);
    setMetrics(newMetrics);
  }, []);

  const handleSlaCritical = useCallback((item) => {
    PapiroConsole.log("AgentWorkspace: SlaCritical received", item);

    if (!item) return;

    const itemDescription = getItemDescription(item);
    const message = `SLA crítico: ${itemDescription}`;

    if (preferences?.slacriticalalertsenabled) {
      // Adicionar notificação
      addNotification({
        id: `sla-critical-${item.itemid}-${Date.now()}`,
        type: 'sla_critical',
        itemId: item.itemid,
        itemType: item.itemtype,
        message,
        item,
        priority: 'high',
      });

      openAlertMessage("warning", message);

      if (preferences?.soundenabled) {
        playNotificationSound();
      }

      showDesktopNotification("SLA Crítico", {
        body: message,
        tag: `sla-critical-${item.itemid}`,
        requireInteraction: true,
      });
    }
  }, [preferences, playNotificationSound, showDesktopNotification, addNotification, getItemDescription]);

  const handleNewMessage = useCallback((data) => {
    PapiroConsole.log("AgentWorkspace: NewMessage received", data);

    if (!data) return;

    const preview = data.messagepreview || data.messagePreview || "Nova mensagem recebida";
    const message = `Nova mensagem: ${preview.substring(0, 50)}${preview.length > 50 ? '...' : ''}`;

    // Adicionar notificação
    addNotification({
      id: `new-message-${data.chatid || data.chatId}-${Date.now()}`,
      type: 'new_message',
      chatId: data.chatid || data.chatId,
      message,
      data,
    });

    if (preferences?.newmessagesoundenabled) {
      playNotificationSound();
    }

    showDesktopNotification("Nova mensagem", {
      body: preview,
      tag: `new-message-${data.chatid || data.chatId}`,
    });
  }, [preferences, playNotificationSound, showDesktopNotification, addNotification]);

  const handleShowError = useCallback((message) => {
    PapiroConsole.log("AgentWorkspace: Error", message);
    if (message) {
      openAlertMessage("error", message.toString());
    }
  }, []);

  // Group by urgency for display
  const getQueueGroupedByUrgency = useCallback(() => {
    const groups = {
      critical: [],
      warning: [],
      normal: [],
      waiting: []
    };

    queue.forEach(item => {
      const group = normalizeUrgencyGroup(item.urgencygroup);
      if (groups[group]) {
        groups[group].push(item);
      } else {
        groups.normal.push(item);
      }
    });

    return groups;
  }, [queue]);

  return (
    <AgentWorkspaceHubContext.Provider value={{
      // Connection
      agentWorkspaceHub,
      setAgentWorkspaceHubConnection,
      isConnected,

      // Queue state
      queue,
      setQueue,
      getQueueGroupedByUrgency,

      // Metrics & preferences
      metrics,
      setMetrics,
      preferences,
      setPreferences,
      agentStatus,
      setAgentStatus,

      // Notifications system
      notifications,
      setAllNotifications,
      handleListNotifications,
      recentNotifications,
      unreadNotificationCount,
      addNotification,
      markNotificationAsRead,
      markAllNotificationsAsRead,
      clearNotifications,

      // Callback registration (for components to subscribe to updates)
      registerQueueChangeCallback,
      registerNewItemCallback,
      registerItemRemovedCallback,
      registerItemUpdatedCallback,
      registerItemClosedCallback,
      registerNotificationCallback,

      // SignalR event handlers (for App.js to bind)
      handleQueueUpdated,
      handleNewItem,
      handleItemRemoved,
      handleItemUpdated,
      handleItemClosed,
      handleMetricsUpdated,
      handleSlaCritical,
      handleNewMessage,
      handleShowError,

      // Utilities
      playNotificationSound,
      showDesktopNotification,
      normalizeUrgencyGroup,
    }}>
      {props.children}

      <CustomizedSnackbars
        variant={alertNotification.variant}
        message={alertNotification.message}
        isOpen={alertNotification.isOpen}
        toClose={closeAlertMessage}
      />
    </AgentWorkspaceHubContext.Provider>
  );
}

export function useAgentWorkspaceHubContext() {
  const context = useContext(AgentWorkspaceHubContext);
  return context;
}

export default AgentWorkspaceHubProvider;
